/* import __COLOCATED_TEMPLATE__ from './seller-profile-network-information.hbs'; */
import { computed } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@ember/component';
import {
  absoluteStoryhunterUrl,
  copyLinkToClipboard
} from "../../utils/url";

/**
 * @type {number}   The length we'll allow before truncating the comment text.
 */
const MAX_REVIEW_LENGTH_UNEXPANDED = 50;

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'section',
  classNames: ['SH-c-seller-profile-network-information'],

  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  session: service(),

  /** @param {User} user */
  user: null,

  workedWithBrands: readOnly('user.workedWithBrands'),

  /** @param {Ember.A} projects */
  projects: null,

  /** @param {Boolean} isViewingSelf */
  isViewingSelf: false,

  /** @param {Function} showFeaturedDigestInModal */
  showFeaturedDigestInModal: () => {},

  hasNoFeaturableReviews: computed('_featuredReviews.[]', 'topUserReviews.isRunning', function() {
    return !this.topUserReviews.isRunning && !this._featuredReviews.length;
  }),

  /**
   * @type {Boolean}    True when we can see the seller's personal website.
   */
  _canViewWebsite: computed('user.profile.personalWebsite', 'session.isAuthenticated', function() {
    return this.get('session.isAuthenticated') && this.get('user.profile.personalWebsite');
  }),

  /**
   * The image object with tooltip and imageUri like:
   *
   * {
   *    imageUri: 'https://cloudinary.com/iejfiejf',
   *    tooltip: 'my tooltip'
   * }
   *
   * @type {Ember.MutableArray<Object>} The list of company brand image URIs.
   */
  _brandImageObjects: computed('workedWithBrands.@each.{displayedName,image}', function () {
    const brandsWorkedWith = this.workedWithBrands;
    const brandImageObjects = [];

    brandsWorkedWith.forEach((brand) => {
      brandImageObjects.push({
        imageUri: brand.image.transforms.profile128,
        tooltip: brand.displayedName,
      });
    });

    return brandImageObjects;
  }),

  /** @type {Ember.A} The featured reviews to show. */
  _featuredReviews: computed('topUserReviews.last.value', function () {
    return this.topUserReviews.last?.value ?? [];
  }),

  /**
   * @type {String}   The URL to the freelancer's profile
   */
  _profileLink: computed('user.slug', function() {
    return `${window.location.origin}/${this.get('user.slug')}`;
  }),

  /**
   * @see MAX_REVIEW_LENGTH_UNEXPANDED
   * @type {Number}
   */
  _reviewLengthCharCount: MAX_REVIEW_LENGTH_UNEXPANDED,

  /**
   * @type {String}   Description text for the share widgets.
   */
  _shareButtonDescriptionText: computed('user.fullName', function() {
    return `View ${this.get('user.fullName')}'s profile on Glimmer, the only platform to find the world's top video talent and seamlessly manage content production.`;
  }),

  /**
   * @type {String}   Description text for the email click specifically.
   */
  _shareButtonEmailDescriptionText: computed('_shareButtonDescriptionText', function() {
    return `${this.get('_shareButtonDescriptionText')}\n\n${window.location.href}`;
  }),

  /**
   * @type {String}   Title text for the share button widget.
   */
  _shareButtonTitleText: computed('user.fullName', function() {
    return `${this.get('user.fullName')} on Glimmer`;
  }),

  /**
   * @type {Array<String>}    Array of hashtags to send to twitter for share widget.
   */
  _shareButtonTwitterHashtags: [
    'filmmaking',
    'freelance',
    'videoproduction'
  ],

  /**
   * @type {String}           The absolute url to this profile for social sharing.
   */
  _shareButtonUrl: computed('user.slug', function() {
    return absoluteStoryhunterUrl(`/${this.get('user.slug')}`);
  }),

  /**
   * Called when a top review company name should be displayed.
   * @param {ProjectReview} review
   */
  reviewBrandOrCompanyName(review) {
    const { brand, company } = review.application;
    return htmlSafe(brand?.displayedName ?? company.name);
  },

  actions: {
    /**
     * Called when the profile link is clicked on
     *
     * @param {MouseEvent} e
     */
    onCopyProfileLinkClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      copyLinkToClipboard(this.get('_profileLink'));

      this.get('applicationStateManager').addToast('Profile link copied to clipboard');
    },

    /**
     * Called when user cliks on one of the reviews to view more.
     *
     * @param {ProjectReview} review
     * @param {MouseEvent} e
     */
    onViewReviewClicked: function(review, e) {
      e.stopPropagation();
      e.preventDefault();

      this.showFeaturedDigestInModal(review.application.projectdigest);
    }
  }
});
